/**
 * Shared UI Telemetry Bootstrap (core)
 *
 * Single implementation of browser-telemetry initialization for BOTH
 * packages/ui trees (src/app and src/dashboard). Each tree's bootstrap file
 * (e.g. src/app/tracing/init.ts) reads its own `env` module and passes the
 * derived values here — core code must not import from either tree.
 *
 * Calls initBrowserTelemetry() from @superblocksteam/telemetry/browser and
 * exposes the resulting instance for UITracing, UIMetrics, hooks, etc.
 */

import type { DeploymentTypeEnum } from "@superblocksteam/shared";
import {
  BatchSpanProcessor,
  initBrowserTelemetry,
  getEnvironmentFromHostname,
  getDefaultPolicy,
  type BrowserTelemetryInstance,
} from "@superblocksteam/telemetry/browser";

import { LocalStorageExporter } from "./LocalStorageExporter";
import { buildPropagateTraceUrls } from "./propagateTraceUrlPatterns";

/**
 * Both trees emit under one service name; tree-specific slicing happens via
 * span attributes, not separate services. (Verified 2026-06-03: no Datadog
 * monitors or dashboards key on a browser-side service other than the RUM
 * SDK's `superblocks-browser`, which is unaffected.)
 */
const SERVICE_NAME = "superblocks-ui";

/** Tree-specific values derived from each tree's `env` module. */
interface UITelemetryConfig {
  /** Falls back to CLOUD semantics inside getDefaultPolicy when undefined. */
  deploymentType: DeploymentTypeEnum;
  /** SUPERBLOCKS_UI_VERSION */
  serviceVersion?: string;
  /** SUPERBLOCKS_UI_TRACING_ENV; falls back to hostname-derived environment. */
  tracingEnv?: string;
  /** SUPERBLOCKS_UI_DP_CONFIG_URL; its origin is excluded from propagation. */
  dpConfigUrl?: string;
}

let telemetry: BrowserTelemetryInstance | undefined;

/**
 * Initialise browser telemetry (idempotent — second calls are no-ops).
 *
 * Called at the module-level side-effect import in each tree's bootstrap so
 * that tracing is available before any application code runs.
 *
 * ignoreUrls is intentionally NOT passed: the telemetry package's
 * DEFAULT_IGNORE_URLS is query-string tolerant and covers fonts/webp/avif/
 * source maps, unlike the narrower override this file historically used.
 */
export function initTelemetry(
  config: UITelemetryConfig,
): BrowserTelemetryInstance {
  if (telemetry) return telemetry;

  const policy = getDefaultPolicy(config.deploymentType);

  const environment =
    config.tracingEnv || getEnvironmentFromHostname(window.location.hostname);

  telemetry = initBrowserTelemetry(
    {
      serviceName: SERVICE_NAME,
      serviceVersion: config.serviceVersion || "0.0.0",
      environment,
      otlpUrl: window.location.origin + "/api",
      propagateTraceUrls: buildPropagateTraceUrls(config.dpConfigUrl),
      resourceAttributes: {
        // Capture origin (e.g. https://pr-123.superblocks.dev) so EE spans can
        // be distinguished from local-dev spans that share the same deployment
        // environment in Datadog. Browser-only — server services have no
        // analogue. `superblocks.environment` is already set by buildResource().
        "superblocks.origin": window.location.origin,
      },
    },
    policy,
  );

  // Register the LocalStorageExporter so the in-app debugger
  // continues to work exactly as before. Both UI trees end up writing the
  // same localStorage keys here, which was already true before this move
  // (the dashboard tree shipped its own copy of this exporter with the same
  // keys). Scope the keys per tree if the debugger ever needs separate data.
  telemetry.addSpanProcessor(
    new BatchSpanProcessor(new LocalStorageExporter()),
  );

  // Flush pending spans when the page unloads so already-ended spans (session,
  // API executions, Clark generations) are exported before the tab goes away.
  // The OTLP fetch uses keepalive, so the export survives page unload (the
  // exporter caps keepalive at ~60KB of pending body; oversized flushes fall
  // back to a normal fetch that unload can cancel).
  const instance = telemetry;
  window.addEventListener("pagehide", () => {
    void instance.forceFlush().catch(() => undefined);
  });

  return telemetry;
}

/**
 * Return the lazily-initialised telemetry instance.
 * Throws if called before initTelemetry().
 */
export function getTelemetry(): BrowserTelemetryInstance {
  if (!telemetry) {
    throw new Error(
      "[Telemetry] Not initialised. Import tracing/init for side-effects first.",
    );
  }
  return telemetry;
}

/**
 * Reset for testing.
 * @internal
 */
export function resetTelemetry(): void {
  telemetry = undefined;
}
